import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BarChart3, Download, FileArchive, Search, RefreshCw, FileText } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function startOfQuarter() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
}

const money = (n) =>
  "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminReports() {
  const [preset, setPreset] = useState("month");
  const [fromDate, setFromDate] = useState(startOfMonth());
  const [toDate, setToDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Invoice manager state
  const [invSearch, setInvSearch] = useState("");
  const [invFrom, setInvFrom] = useState("");
  const [invTo, setInvTo] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [downloadingZip, setDownloadingZip] = useState(false);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "month") { setFromDate(startOfMonth()); setToDate(todayISO()); }
    else if (p === "quarter") { setFromDate(startOfQuarter()); setToDate(todayISO()); }
    else if (p === "year") {
      const y = new Date().getFullYear();
      setFromDate(`${y}-01-01`); setToDate(todayISO());
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/gst`, { params: { from_date: fromDate, to_date: toDate } });
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load GST report");
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    setInvLoading(true);
    try {
      const { data } = await api.get(`/admin/invoices`, {
        params: { q: invSearch || undefined, from_date: invFrom || undefined, to_date: invTo || undefined },
      });
      setInvoices(data.invoices);
      setSelected({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load invoices");
    } finally {
      setInvLoading(false);
    }
  };

  useEffect(() => { loadReport(); loadInvoices(); /* eslint-disable-next-line */ }, []);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("w2e_token");
      const url = `${API_BASE}/api/admin/reports/gst.csv?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("CSV download failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `gst-report-${fromDate}-to-${toDate}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("CSV downloaded");
    } catch (err) { toast.error(err.message || "CSV failed"); }
  };

  const downloadInvoice = async (id, num) => {
    try {
      const token = localStorage.getItem("w2e_token");
      const res = await fetch(`${API_BASE}/api/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${num || id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { toast.error(err.message || "Invoice failed"); }
  };

  const downloadZip = async (useSelected) => {
    setDownloadingZip(true);
    try {
      const token = localStorage.getItem("w2e_token");
      let url = `${API_BASE}/api/admin/invoices/zip?`;
      const ids = Object.keys(selected).filter((k) => selected[k]);
      if (useSelected && ids.length > 0) {
        url += `ids=${ids.join(",")}`;
      } else {
        const p = new URLSearchParams();
        if (invSearch) p.set("q", invSearch);
        if (invFrom) p.set("from_date", invFrom);
        if (invTo) p.set("to_date", invTo);
        url += p.toString();
      }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "ZIP download failed");
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoices-${(invFrom || "all")}-to-${(invTo || "now")}.zip`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("ZIP downloaded");
    } catch (err) {
      toast.error(err.message || "ZIP failed");
    } finally {
      setDownloadingZip(false);
    }
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const toggleAll = (checked) => {
    const s = {};
    if (checked) invoices.forEach((i) => (s[i.id] = true));
    setSelected(s);
  };

  const totals = report?.totals || {};
  const intra = report?.intra_state || {};
  const inter = report?.inter_state || {};

  return (
    <DashboardLayout admin>
      <div className="mb-6">
        <div className="section-label">Finance</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">GST Reports & Invoices</h1>
        <p className="text-sm text-slate-600 mt-1">Filter, review and export tax data for GST filing.</p>
      </div>

      <Tabs defaultValue="report" className="space-y-6">
        <TabsList>
          <TabsTrigger value="report" data-testid="reports-tab-report">
            <BarChart3 className="w-4 h-4 mr-1.5" /> GST Report
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="reports-tab-invoices">
            <FileText className="w-4 h-4 mr-1.5" /> Invoice Manager
          </TabsTrigger>
        </TabsList>

        {/* GST REPORT */}
        <TabsContent value="report" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex gap-2 flex-wrap">
                {[
                  ["month", "This Month"],
                  ["quarter", "This Quarter"],
                  ["year", "This Year"],
                  ["custom", "Custom"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => applyPreset(k)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                      preset === k ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`report-preset-${k}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 items-end">
                <div><Label>From</Label><Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreset("custom"); }} className="h-9" data-testid="report-from" /></div>
                <div><Label>To</Label><Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPreset("custom"); }} className="h-9" data-testid="report-to" /></div>
                <Button onClick={loadReport} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-9" data-testid="report-refresh">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Refresh</>}
                </Button>
                <Button onClick={downloadCsv} variant="outline" className="h-9" data-testid="report-download-csv">
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Total Orders" value={report?.count ?? 0} />
            <SummaryCard label="Total Taxable" value={money(totals.taxable)} accent />
            <SummaryCard label="Total GST Collected" value={money((totals.cgst || 0) + (totals.sgst || 0) + (totals.igst || 0))} />
            <SummaryCard label="Gross Collected" value={money(totals.total)} accent />
          </div>

          {/* Split totals */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="section-label">Intra-state (Rajasthan)</div>
              <div className="font-display font-semibold text-xl text-[#0A192F] mt-1">CGST + SGST</div>
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-slate-500">Orders</div><div className="text-right font-semibold">{intra.count || 0}</div>
                <div className="text-slate-500">Taxable</div><div className="text-right">{money(intra.taxable)}</div>
                <div className="text-slate-500">CGST 9%</div><div className="text-right">{money(intra.cgst)}</div>
                <div className="text-slate-500">SGST 9%</div><div className="text-right">{money(intra.sgst)}</div>
                <div className="text-slate-500 border-t border-slate-200 pt-2 font-semibold">Total</div>
                <div className="text-right border-t border-slate-200 pt-2 font-bold text-[#0A192F]">{money(intra.total)}</div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="section-label">Inter-state (Other states)</div>
              <div className="font-display font-semibold text-xl text-[#0A192F] mt-1">IGST</div>
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-slate-500">Orders</div><div className="text-right font-semibold">{inter.count || 0}</div>
                <div className="text-slate-500">Taxable</div><div className="text-right">{money(inter.taxable)}</div>
                <div className="text-slate-500">IGST 18%</div><div className="text-right">{money(inter.igst)}</div>
                <div className="text-slate-500 border-t border-slate-200 pt-2 font-semibold">Total</div>
                <div className="text-right border-t border-slate-200 pt-2 font-bold text-[#0A192F]">{money(inter.total)}</div>
              </div>
            </div>
          </div>

          {/* Detailed table */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-semibold text-[#0A192F]">Order-level GST breakdown</div>
              <span className="text-xs text-slate-500">{report?.count || 0} orders</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="p-2 text-left">Order No</th>
                    <th className="p-2 text-left">Customer</th>
                    <th className="p-2 text-left">Service</th>
                    <th className="p-2 text-left">Billing State</th>
                    <th className="p-2 text-left">Place of Supply</th>
                    <th className="p-2 text-right">Taxable</th>
                    <th className="p-2 text-right">CGST</th>
                    <th className="p-2 text-right">SGST</th>
                    <th className="p-2 text-right">IGST</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-left">Paid On</th>
                  </tr>
                </thead>
                <tbody>
                  {(report?.orders || []).map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-row-${r.order_number}`}>
                      <td className="p-2 font-mono">{r.order_number}</td>
                      <td className="p-2"><div className="font-medium">{r.customer_name}</div><div className="text-slate-500">{r.customer_email}</div></td>
                      <td className="p-2">{r.service_name}</td>
                      <td className="p-2">{r.billing_state || "—"}</td>
                      <td className="p-2">{r.place_of_supply || "—"}</td>
                      <td className="p-2 text-right">{money(r.taxable)}</td>
                      <td className="p-2 text-right">{r.cgst ? money(r.cgst) : "—"}</td>
                      <td className="p-2 text-right">{r.sgst ? money(r.sgst) : "—"}</td>
                      <td className="p-2 text-right">{r.igst ? money(r.igst) : "—"}</td>
                      <td className="p-2 text-right font-semibold text-[#0A192F]">{money(r.total)}</td>
                      <td className="p-2">{r.payment_date || "—"}</td>
                    </tr>
                  ))}
                  {(!report?.orders || report.orders.length === 0) && (
                    <tr><td colSpan={11} className="p-8 text-center text-slate-500">No paid orders in the selected period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* INVOICE MANAGER */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[220px]">
                <Label>Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Order # / name / email / mobile"
                    value={invSearch}
                    onChange={(e) => setInvSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="invoice-search"
                    onKeyDown={(e) => e.key === "Enter" && loadInvoices()}
                  />
                </div>
              </div>
              <div><Label>From</Label><Input type="date" value={invFrom} onChange={(e) => setInvFrom(e.target.value)} className="h-9" data-testid="invoice-from" /></div>
              <div><Label>To</Label><Input type="date" value={invTo} onChange={(e) => setInvTo(e.target.value)} className="h-9" data-testid="invoice-to" /></div>
              <Button onClick={loadInvoices} disabled={invLoading} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-9" data-testid="invoice-search-btn">
                {invLoading ? "…" : "Search"}
              </Button>
              <Button
                onClick={() => downloadZip(selectedCount > 0)}
                disabled={downloadingZip || invoices.length === 0}
                variant="outline"
                className="h-9"
                data-testid="invoice-zip-btn"
              >
                <FileArchive className="w-4 h-4 mr-1" />
                {downloadingZip ? "Zipping…" : selectedCount > 0 ? `Download ZIP (${selectedCount})` : "Download all as ZIP"}
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="p-2 text-left w-8">
                      <input
                        type="checkbox"
                        checked={invoices.length > 0 && selectedCount === invoices.length}
                        onChange={(e) => toggleAll(e.target.checked)}
                        data-testid="invoice-select-all"
                      />
                    </th>
                    <th className="p-2 text-left">Order No</th>
                    <th className="p-2 text-left">Customer</th>
                    <th className="p-2 text-left">Service</th>
                    <th className="p-2 text-left">State</th>
                    <th className="p-2 text-right">Amount</th>
                    <th className="p-2 text-left">Paid On</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${r.order_number}`}>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={!!selected[r.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                          data-testid={`invoice-select-${r.order_number}`}
                        />
                      </td>
                      <td className="p-2 font-mono">{r.order_number}</td>
                      <td className="p-2"><div className="font-medium">{r.customer_name}</div><div className="text-xs text-slate-500">{r.customer_email}</div></td>
                      <td className="p-2">{r.service_name}</td>
                      <td className="p-2 text-xs">{r.billing_state || "—"}</td>
                      <td className="p-2 text-right font-semibold text-[#0A192F]">{money(r.amount)}</td>
                      <td className="p-2 text-xs">{r.payment_date || "—"}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => downloadInvoice(r.id, r.order_number)} data-testid={`invoice-dl-${r.order_number}`}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-slate-500">No invoices match the filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-[#1E3A8A]/30 bg-[#1E3A8A]/5" : "border-slate-200 bg-white"}`}>
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-bold text-xl text-[#0A192F] mt-1">{value}</div>
    </div>
  );
}
